// src/hooks/useCurrentLead.ts
import { useCallback, useMemo } from "react";

import type { Dispatch, SetStateAction } from "react";

import type { ExtractedLeadV2 } from "../types/lead";
import type { UpdateLeadFieldFn } from "../types/leadActions";

type Inputs = {
  extracted: ExtractedLeadV2 | null | undefined;
  updateCurrentLeadField: UpdateLeadFieldFn;
  phoneDraft: string;
  setPhoneDraft: Dispatch<SetStateAction<string>>;
  showProductDetails: boolean;
  setShowProductDetails: Dispatch<SetStateAction<boolean>>;
  showLocationDetails: boolean;
  setShowLocationDetails: Dispatch<SetStateAction<boolean>>;
};

export function normalizePhoneOrEmpty(input: string): string {
  const digits = (input || "").replace(/\D/g, "");

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 7) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return "";
}

export function useCurrentLead(inputs: Inputs) {
  const {
    extracted,
    updateCurrentLeadField,
    phoneDraft,
    setPhoneDraft,
    showProductDetails,
    setShowProductDetails,
    showLocationDetails,
    setShowLocationDetails,
  } = inputs;

  const e = useMemo(() => {
    return extracted || ({} as ExtractedLeadV2);
  }, [extracted]);

  const setE = useCallback(
    (next: Partial<ExtractedLeadV2>) => {
      for (const [key, value] of Object.entries(next)) {
        updateCurrentLeadField(
          key as keyof ExtractedLeadV2,
          (value as ExtractedLeadV2[keyof ExtractedLeadV2]) ?? null
        );
      }
    },
    [updateCurrentLeadField]
  );

  return {
    e,
    setE,
    updateCurrentLeadField,
    phoneDraft,
    setPhoneDraft,
    normalizePhoneOrEmpty,
    showProductDetails,
    setShowProductDetails,
    showLocationDetails,
    setShowLocationDetails,
  };
}

export type LeadBundle = ReturnType<typeof useCurrentLead>;
